import { useState } from 'react'
import { useEncryptionContext } from '../../contexts/EncryptionContext'
import { useFamilyStore } from '../../stores/familyStore'
import { useEventsStore } from '../../stores/eventsStore'
import { convex } from '../../lib/convex'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { Button } from '../ui/Button'

/**
 * One-click migration of existing plaintext data to E2EE.
 * Reads children names, event titles/notes, encrypts them, and saves back.
 */
export function EncryptionMigration() {
  const { isReady, encrypt, isEncrypted: checkEncrypted } = useEncryptionContext()
  const { family, children } = useFamilyStore()
  const { events } = useEventsStore()

  const [migrating, setMigrating] = useState(false)
  const [result, setResult] = useState<{ children: number; events: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const encryptionActive = family?.encryption_active_since != null

  if (!isReady || !encryptionActive) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <p className="text-xs text-yellow-800">
          Szyfrowanie jeszcze nieaktywne — czekamy aż wszyscy członkowie rodziny zalogują się
          na swoich urządzeniach. Migracja istniejących danych ruszy automatycznie po aktywacji.
        </p>
      </div>
    )
  }

  // Count plaintext items
  const plaintextChildren = children.filter(c => !checkEncrypted(c.name))
  const plaintextEvents = events.filter(e => !checkEncrypted(e.title))
  const totalPlaintext = plaintextChildren.length + plaintextEvents.length

  if (totalPlaintext === 0 && !result) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
        <p className="text-xs text-green-800 flex items-center">
          <span className="mr-2">&#x2705;</span>
          All data is encrypted. Nothing to migrate.
        </p>
      </div>
    )
  }

  const handleMigrate = async () => {
    if (!family) return
    setMigrating(true)
    setError(null)

    let migratedChildren = 0
    let migratedEvents = 0

    try {
      // Migrate children names
      for (const child of plaintextChildren) {
        const encryptedName = await encrypt(child.name)
        if (encryptedName) {
          await convex.mutation(api.families.mutations.updateChild, {
            childId: child.id as Id<"children">,
            familyId: family.id as Id<"families">,
            name: encryptedName,
          })
          migratedChildren++
        }
      }

      // Migrate event titles and notes
      for (const event of plaintextEvents) {
        const encryptedTitle = await encrypt(event.title)
        const encryptedNotes = event.notes ? await encrypt(event.notes) : undefined

        if (encryptedTitle) {
          await convex.mutation(api.events.mutations.updateEvent, {
            eventId: event.id as Id<"events">,
            title: encryptedTitle,
            notes: encryptedNotes ?? undefined,
          })
          migratedEvents++
        }
      }

      setResult({ children: migratedChildren, events: migratedEvents })
    } catch (err: any) {
      setError(err.message || 'Migration failed')
    } finally {
      setMigrating(false)
    }
  }

  if (result) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
        <p className="text-xs text-green-800">
          Migration complete: {result.children} children, {result.events} events encrypted.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
      <p className="text-xs text-blue-800">
        <strong>{totalPlaintext}</strong> items are stored as plaintext ({plaintextChildren.length} children, {plaintextEvents.length} events).
        Click below to encrypt them.
      </p>

      {error && (
        <p className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">{error}</p>
      )}

      <Button
        onClick={handleMigrate}
        disabled={migrating}
        size="sm"
      >
        {migrating ? (
          <>
            <div className="animate-spin h-3 w-3 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
            Encrypting...
          </>
        ) : (
          'Encrypt existing data'
        )}
      </Button>
    </div>
  )
}
